import { useCallback, useEffect, useState } from 'react';
import { useHasBeenFocused } from './useHasBeenFocused';
import { useIsFocused } from './useIsFocused';
import { usePropertyNormalizationRules } from './usePropertyNormalizationRules';
import { usePropertyValue } from './usePropertyValue';
const applyNormalizationRules = (value, rules, options = {}) => {
  if (!Array.isArray(rules) || !rules || !value) {
    return {
      normalizedValue: value,
      rulesApplied: []
    };
  }
  const rulesApplied = [];
  let normalizedValue = value;
  for (const rule of rules) {
    var _options$shouldSkip;
    if ((_options$shouldSkip = options.shouldSkip) !== null && _options$shouldSkip !== void 0 && _options$shouldSkip.call(options, rule)) {
      continue;
    }
    if (!rule.regex.test(normalizedValue)) {
      continue;
    }
    const newValue = normalizedValue.replace(rule.regex, match => rule.process(match));
    if (newValue !== normalizedValue) {
      rulesApplied.push(rule.name);
      normalizedValue = newValue;
    }
  }
  return {
    normalizedValue,
    rulesApplied
  };
};
export const usePropertyValueFormatting = ({
  onChange,
  onBlur,
  onKeyDown
}) => {
  const [appliedRules, setAppliedRules] = useState([]);
  const hasBeenFocused = useHasBeenFocused();
  const isFocused = useIsFocused();
  const value = usePropertyValue();
  const {
    data: normalizationRules
  } = usePropertyNormalizationRules({
    skip: !hasBeenFocused
  });
  const handleChangeWithFormatting = useCallback(event => {
    const newValue = event.target.value;
    const {
      normalizedValue,
      rulesApplied
    } = applyNormalizationRules(newValue, normalizationRules, {
      shouldSkip: rule => isFocused && rule.name === 'TRIM_WHITESPACE'
    });
    setAppliedRules(rulesApplied);

    // No change occurred after normalization, don't propagate the event
    if (normalizedValue === value) {
      return;
    }
    onChange(Object.assign({}, event, {
      target: Object.assign({}, event.target, {
        value: normalizedValue
      })
    }));
  }, [normalizationRules, isFocused, value, onChange]);
  const applyTrimWhitespace = useCallback(() => {
    const trimRule = normalizationRules === null || normalizationRules === void 0 ? void 0 : normalizationRules.find(rule => rule.name === 'TRIM_WHITESPACE');
    if (!trimRule || !value) {
      return;
    }
    const {
      normalizedValue,
      rulesApplied
    } = applyNormalizationRules(value, [trimRule]);
    if (rulesApplied.length > 0 && normalizedValue !== value) {
      onChange({
        target: {
          value: normalizedValue
        }
      });
      setAppliedRules(rulesApplied);
      return;
    }
  }, [normalizationRules, value, onChange]);
  const handleBlurWithTrimming = useCallback(event => {
    applyTrimWhitespace();
    onBlur === null || onBlur === void 0 || onBlur(event);
  }, [applyTrimWhitespace, onBlur]);
  const handleKeyDownWithTrimming = useCallback(event => {
    if (event.key === 'Enter') {
      applyTrimWhitespace();
    }
    onKeyDown === null || onKeyDown === void 0 || onKeyDown(event);
  }, [applyTrimWhitespace, onKeyDown]);
  useEffect(() => {
    if (!isFocused && appliedRules.length > 0) {
      setAppliedRules([]);
    }
  }, [isFocused, appliedRules]);
  return {
    onChange: handleChangeWithFormatting,
    onBlur: handleBlurWithTrimming,
    onKeyDown: handleKeyDownWithTrimming,
    normalizationRules: appliedRules
  };
};